import { useEffect, useState } from "react";
import size from "lodash/size";

import { useCurrentUser } from "globals/hooks";
import { Contact } from "types";

export type SelectedPassenger = {
  id: string;
  email: string;
  mobilePhone: string;
  firstName: string;
  lastName: string;
};

enum DefaultPassengerType {
  BookingContact = "Booking Contact",
  LinkedPassenger = "Linked Passenger",
  PreferredPassenger = "Preferred Passenger",
}

function useDefaultPassenger(initialContact?: Contact) {
  // state
  const [selectedPassenger, setSelectedPassenger] =
    useState<SelectedPassenger>(null);
  const [passengerType, setPassengerType] =
    useState<DefaultPassengerType | null>(null);

  // hooks
  const currentUser = useCurrentUser();

  // effects
  useEffect(() => {
    if (!currentUser) return;

    if (initialContact && !selectedPassenger) {
      setSelectedPassenger(initialContact);
      return;
    }

    if (selectedPassenger) return;

    // Priority 1: Use preferred passenger if set
    if (currentUser.preferredPassenger) {
      const preferred = currentUser.preferredPassenger;
      setSelectedPassenger({
        id: preferred.id,
        firstName: preferred.firstName,
        lastName: preferred.lastName,
        email: preferred.email,
        mobilePhone: preferred.mobilePhone,
      });
      setPassengerType(DefaultPassengerType.PreferredPassenger);
      return;
    }

    // Priority 2: Use single linked passenger if only one exists
    if (size(currentUser.linkedPassengers) === 1) {
      const linkedPassenger = currentUser.linkedPassengers[0];
      setSelectedPassenger({
        id: linkedPassenger.id,
        firstName: linkedPassenger.firstName,
        lastName: linkedPassenger.lastName,
        email: linkedPassenger.email,
        mobilePhone: linkedPassenger.mobilePhone,
      });
      setPassengerType(DefaultPassengerType.LinkedPassenger);
      return;
    }

    // Priority 3: Default to booking contact
    setSelectedPassenger({
      id: currentUser.id,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      mobilePhone: currentUser.mobilePhone,
    });
    setPassengerType(DefaultPassengerType.BookingContact);
  }, [currentUser, selectedPassenger, initialContact]);

  if (!currentUser) return null;

  return {
    selectedPassenger,
    setSelectedPassenger,
    selectedPassengerIsBookingContact: selectedPassenger?.id === currentUser.id,
    passengerType,
  };
}

export { useDefaultPassenger };
